/**
 * Minimal promise-wrapped IndexedDB used by the whole local-first layer.
 * Two stores:
 *   objects -> { path, blob, contentType, size, updated }   (the "bucket")
 *   docs    -> { key, collection, id, data, createdAt }     (the "firestore")
 */
const DB_NAME = 'pcake-studio';
const DB_VERSION = 1;
export const OBJECTS = 'objects';
export const DOCS = 'docs';

let dbPromise = null;

export function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(OBJECTS)) {
        db.createObjectStore(OBJECTS, { keyPath: 'path' });
      }
      if (!db.objectStoreNames.contains(DOCS)) {
        const s = db.createObjectStore(DOCS, { keyPath: 'key' });
        s.createIndex('collection', 'collection', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function run(store, mode, fn) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const os = tx.objectStore(store);
    let result;
    try { result = fn(os); } catch (e) { reject(e); return; }
    tx.oncomplete = () => resolve(result && result.__req ? result.__req.result : result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));
}

const wrap = req => ({ __req: req });

export const idbGet = (store, key) => run(store, 'readonly', os => wrap(os.get(key)));
export const idbPut = (store, value) => run(store, 'readwrite', os => wrap(os.put(value)));
export const idbDelete = (store, key) => run(store, 'readwrite', os => wrap(os.delete(key)));
export const idbGetAll = (store) => run(store, 'readonly', os => wrap(os.getAll()));
export const idbClear = (store) => run(store, 'readwrite', os => wrap(os.clear()));

export async function idbEstimate() {
  if (!navigator.storage?.estimate) return { usage: 0, quota: 0 };
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota };
}
